import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { EMPLOYER_TOUR_STORAGE_KEYS } from "./tourConfig";

interface EmployerTourContextType {
  isTourRunning: boolean;
  startTour: () => void;
  stopTour: () => void;
  completeTour: () => void;
  skipTour: () => void;
  isTourCompleted: boolean;
  isTourDismissed: boolean;
  shouldShowWelcome: boolean;
  dismissWelcome: () => void;
}

const EmployerTourContext = createContext<EmployerTourContextType | undefined>(undefined);

export function useEmployerTour() {
  const context = useContext(EmployerTourContext);
  if (!context) {
    throw new Error("useEmployerTour must be used within EmployerTourProvider");
  }
  return context;
}

export function EmployerTourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [isTourCompleted, setIsTourCompleted] = useState(false);
  const [isTourDismissed, setIsTourDismissed] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    const completed = localStorage.getItem(EMPLOYER_TOUR_STORAGE_KEYS.completed(user.id)) === "true";
    const dismissed = localStorage.getItem(EMPLOYER_TOUR_STORAGE_KEYS.dismissed(user.id)) === "true";
    setIsTourCompleted(completed);
    setIsTourDismissed(dismissed);
  }, [user?.id]);

  const startTour = () => setIsTourRunning(true);
  const stopTour = () => setIsTourRunning(false);

  const completeTour = () => {
    if (!user?.id) return;
    localStorage.setItem(EMPLOYER_TOUR_STORAGE_KEYS.completed(user.id), "true");
    setIsTourCompleted(true);
    setIsTourRunning(false);
  };

  const skipTour = () => {
    if (!user?.id) return;
    localStorage.setItem(EMPLOYER_TOUR_STORAGE_KEYS.dismissed(user.id), "true");
    setIsTourDismissed(true);
    setIsTourRunning(false);
  };

  const dismissWelcome = () => {
    if (!user?.id) return;
    localStorage.setItem(EMPLOYER_TOUR_STORAGE_KEYS.dismissed(user.id), "true");
    setIsTourDismissed(true);
  };

  const shouldShowWelcome = !isTourCompleted && !isTourDismissed;

  return (
    <EmployerTourContext.Provider value={{
      isTourRunning, startTour, stopTour, completeTour, skipTour,
      isTourCompleted, isTourDismissed, shouldShowWelcome, dismissWelcome,
    }}>
      {children}
    </EmployerTourContext.Provider>
  );
}
